import React from 'react';
import { DayContextCard, ChoiceSheet } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import {
  MapPin, Clock, CheckCircle2, AlertTriangle, CalendarOff, CalendarDays, Building2,
  LayoutGrid, Sparkles,
} from 'lucide-react';
import { ALL_CLINICS, AUTO_CLINIC } from '../../hooks/useActiveClinic';
import { minutesToHHMM, hoursRange } from '../../screens/Reception/Reception.shared';
import { useReceptionStrings } from '../../lib/receptionStrings';

// ─────────────────────────────────────────────────────────────────────────────
// The mobile پذیرش header's context row (DOCTOR_MOBILE_RECEPTION_REDESIGN §4.2):
// one white-alpha inset card on the gradient, two rows.
//
//   PlaceLine — «کجا هستم»: where the presence schedule puts the doctor on the
//               selected day (`resolvePlace`). With more than one clinic it is
//               also the trigger for the clinic lens; with one it only informs
//               (§2.1: absent, never disabled).
//   DateBar   — ‹ day › with the label opening the week sheet. It replaces the
//               navigator + week strip + «N نوبت پیش رو» stack that used to cost
//               ~130px of a phone's height.
// ─────────────────────────────────────────────────────────────────────────────

const PLACE_ICON = {
  in_shift: MapPin,
  between: Clock,
  before_first: Clock,
  done: CheckCircle2,
  overlap: AlertTriangle,
  no_shift: CalendarOff,
  other_day: CalendarDays,
  unknown: Building2,
};

/** The place line's sentence, or null when there is nothing to claim. Exported for tests. */
export function placeSentence(place, { rs, clinicName, time, weekday, isToday, formatNumber, lensLabel, digits = (x) => x }) {
  const w = place?.window;
  switch (place?.state) {
    case 'in_shift':     return rs('dashboard.place.inShift', { clinic: clinicName(w.clinicId), end: time(w.end) });
    case 'between':      return rs('dashboard.place.between', { clinic: clinicName(w.clinicId), start: time(w.start) });
    case 'before_first': return rs('dashboard.place.beforeFirst', { clinic: clinicName(w.clinicId), start: time(w.start) });
    case 'done':         return rs('dashboard.place.done', { clinic: clinicName(w.clinicId) });
    case 'overlap':      return rs('dashboard.place.overlap', { count: formatNumber(place.clinicIds.length) });
    case 'no_shift':     return rs(isToday ? 'dashboard.place.noShiftToday' : 'dashboard.place.noShiftDay');
    case 'other_day': {
      const first = place.day[0];
      const text = rs('dashboard.place.otherDay', {
        weekday, clinic: clinicName(first.clinicId), hours: hoursRange(first.start, first.end, digits),
      });
      const others = place.clinicIds.length - 1;
      return others > 0 ? `${text} ${rs('dashboard.place.more', { count: formatNumber(others) })}` : text;
    }
    case 'unknown':      return lensLabel;   // the lens as it stands — no claim about the schedule
    default:             return null;
  }
}

const ReceptionContextCard = ({
  place, clinic, counts, isToday, selMs, dateLabel,
  onPrev, onNext, onToday, onOpenWeek,
}) => {
  const { t, formatDigits, formatNumber, formatWeekday } = useTranslation();
  const rs = useReceptionStrings();
  const [lensOpen, setLensOpen] = React.useState(false);

  const multi = clinic.clinics.length > 1;
  const clinicName = (id) => clinic.clinics.find((c) => c.id === id)?.name || t('common.labels.clinic');
  const lensLabel = clinic.isAll ? t('dashboard.clinic.all') : (clinic.activeClinic?.name || t('common.labels.clinic'));

  const sentence = placeSentence(place, {
    rs, clinicName, isToday, formatNumber, lensLabel,
    time: (m) => formatDigits(minutesToHHMM(m)),
    digits: formatDigits,
    weekday: formatWeekday(selMs, 'long'),
  });

  // What the lens is doing, when that is not simply «following the schedule to
  // the clinic the sentence already names». A manual pick is shown by name so
  // the doctor can see the schedule and the list disagree on purpose.
  const lensPill = !multi || place?.state === 'unknown' ? null
    : clinic.mode === 'auto' ? rs('dashboard.clinic.autoBadge')
      : lensLabel;

  const muted = place?.state === 'no_shift';

  const lensOptions = multi ? [
    { id: AUTO_CLINIC, name: rs('dashboard.clinic.auto'), meta: rs('dashboard.clinic.autoSub'), icon: Sparkles, checked: clinic.mode === 'auto' },
    ...clinic.clinics.map((c) => ({
      id: c.id,
      name: c.name || t('common.labels.clinic'),
      meta: [c.city, counts?.has(c.id) ? t('dashboard.clinic.countToday', { count: formatNumber(counts.get(c.id)) }) : null].filter(Boolean).join(' · '),
      icon: Building2,
      checked: clinic.mode === 'pick' && clinic.activeId === c.id,
    })),
    { id: ALL_CLINICS, name: t('dashboard.clinic.all'), meta: t('dashboard.clinic.allSub'), icon: LayoutGrid, checked: clinic.mode === 'all', quiet: true },
  ] : [];

  // The card itself is the kit's (DayContextCard, shared with the clinic's
  // phone پذیرش — PM-0052); what it SAYS is decided here.
  return (
    <>
      <DayContextCard
        line={sentence == null ? null : {
          icon: PLACE_ICON[place?.state] || Building2,
          text: sentence,
          hint: muted ? rs('dashboard.place.noShiftHint') : null,
          pill: lensPill,
          tone: place?.state === 'overlap' ? 'warn' : muted ? 'muted' : undefined,
          state: place?.state,
          live: true,
          ariaLabel: rs('dashboard.place.aria'),
          onOpen: multi ? () => setLensOpen(true) : undefined,
          openLabel: t('dashboard.clinic.pick'),
        }}
        dateLabel={dateLabel}
        isToday={isToday}
        onPrev={onPrev}
        onNext={onNext}
        onToday={onToday}
        onOpenWeek={onOpenWeek}
        labels={{
          prevDay: t('dashboard.date.prevDay'),
          nextDay: t('dashboard.date.nextDay'),
          today: t('dashboard.date.today'),
          backToToday: t('dashboard.date.backToToday'),
          pickDay: rs('dashboard.week.title'),
        }}
      />

      {multi && (
        <ChoiceSheet
          open={lensOpen}
          onClose={() => setLensOpen(false)}
          title={t('dashboard.clinic.label')}
          closeLabel={t('common.actions.close')}
          testIdPrefix="reception-clinic"
          groups={[{ key: 'clinic', label: t('dashboard.clinic.pick'), onPick: (id) => clinic.setActive(id), options: lensOptions }]}
        />
      )}
    </>
  );
};

export default ReceptionContextCard;
